import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Trash2, Edit2, Bell, Phone, Mail, Facebook, Building, FolderPlus, ChevronDown, ChevronRight, Folder, Check, X, Star, Search, Upload, History, RotateCcw, Tag, Cake, Loader2, Clock } from 'lucide-react';
import { ReminderField, getBrowserTimezone } from '../components/ReminderField';
import { ContactImportDialog } from '../components/contacts/ContactImportDialog';
import { openExternalLink } from '../utils/pwaUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('All');
  const [showFollowUpOnly, setShowFollowUpOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [isManageFoldersOpen, setIsManageFoldersOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedContact, setExpandedContact] = useState(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [imports, setImports] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    phone: '',
    email: '',
    facebook_url: '',
    notes: '',
    picture_url: '',
    tags: '',
    folder: 'General',
    followup_date: '',
    followup_time: '',
    followup_reminder: 'none',
  });

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts`, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    }
  }, [getAuthHeaders]);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts/folders`, getAuthHeaders());
      setFolders(response.data);
    } catch (error) {
      console.error('Failed to load folders');
    }
  }, [getAuthHeaders]);

  const fetchImports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts/imports`, getAuthHeaders());
      setImports(response.data || []);
    } catch (error) {
      console.error('Failed to load import history');
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchContacts();
    fetchFolders();
    fetchImports();
  }, [fetchContacts, fetchFolders, fetchImports]);

  // Keep history fresh while any import is running in the background
  useEffect(() => {
    const running = imports.some((i) => i.status === 'processing');
    if (!running) return;
    const id = setInterval(async () => {
      const res = await axios.get(`${API}/contacts/imports`, getAuthHeaders()).catch(() => null);
      if (!res) return;
      setImports(res.data || []);
      const stillRunning = (res.data || []).some((i) => i.status === 'processing');
      if (!stillRunning) { fetchContacts(); } // an import just finished — refresh list
    }, 2500);
    return () => clearInterval(id);
  }, [imports, getAuthHeaders, fetchContacts]);

  const undoImport = async (batchId) => {
    try {
      const res = await axios.post(`${API}/contacts/imports/${batchId}/undo`, {}, getAuthHeaders());
      toast.success(`${res.data.removed} imported contacts removed`);
      fetchContacts();
      fetchImports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo import');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        business: formData.business,
        phone: formData.phone,
        email: formData.email,
        facebook_url: formData.facebook_url,
        notes: formData.notes,
        picture_url: formData.picture_url,
        folder: formData.folder,
        followup_date: formData.followup_date || '',
        followup_time: formData.followup_date ? (formData.followup_time || '09:00') : '',
        reminder_offset: formData.followup_date ? (formData.followup_reminder || 'none') : 'none',
        timezone: getBrowserTimezone(),
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };

      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.contact_id}`, payload, getAuthHeaders());
        toast.success('Contact updated!');
      } else {
        await axios.post(`${API}/contacts`, payload, getAuthHeaders());
        toast.success('Contact created!');
      }
      fetchContacts();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save contact');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`, getAuthHeaders());
      toast.success('Contact deleted!');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  // Toggle follow-up status for a contact
  const toggleFollowUp = async (contactId) => {
    try {
      const response = await axios.post(`${API}/contacts/${contactId}/toggle-followup`, {}, getAuthHeaders());
      setContacts(contacts.map(c => 
        c.contact_id === contactId ? { ...c, follow_up: response.data.follow_up } : c
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update follow-up status');
    }
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      business: contact.business || '',
      phone: contact.phone || '',
      email: contact.email || '',
      facebook_url: contact.facebook_url || '',
      notes: contact.notes || '',
      picture_url: contact.picture_url || '',
      folder: contact.folder || 'General',
      tags: (contact.tags || []).join(', '),
      followup_date: contact.followup_date || '',
      followup_time: contact.followup_time || '',
      followup_reminder: contact.reminder_offset || 'none',
    });
    setIsDialogOpen(true);
  };

  // Deep link from Home "Today's Follow-Ups": /contacts?open=<contact_id>
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || contacts.length === 0) return;
    const c = contacts.find((x) => x.contact_id === openId);
    if (c) openEditDialog(c);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, searchParams]);

  const resetForm = () => {
    setFormData({
      name: '',
      business: '',
      phone: '',
      email: '',
      facebook_url: '',
      notes: '',
      picture_url: '',
      tags: '',
      folder: selectedFolder === 'All' ? 'General' : selectedFolder,
      followup_date: '',
      followup_time: '',
      followup_reminder: 'none',
    });
    setEditingContact(null);
  };

  const completeFollowUp = async (contactId) => {
    try {
      await axios.post(`${API}/contacts/${contactId}/complete-followup`, {}, getAuthHeaders());
      toast.success('Follow-up marked complete!');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to complete follow-up');
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/contacts/folders`, { name: newFolderName }, getAuthHeaders());
      toast.success('Folder created!');
      fetchFolders();
      setNewFolderName('');
      setIsFolderDialogOpen(false);
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const startEditFolder = (folder) => {
    setEditingFolder(folder.folder_id);
    setEditFolderName(folder.name);
  };

  const cancelEditFolder = () => {
    setEditingFolder(null);
    setEditFolderName('');
  };

  const saveEditFolder = async (folderId) => {
    if (!editFolderName.trim()) {
      toast.error('Folder name cannot be empty');
      return;
    }
    try {
      await axios.put(`${API}/contacts/folders/${folderId}`, { name: editFolderName }, getAuthHeaders());
      toast.success('Folder renamed!');
      fetchFolders();
      setEditingFolder(null);
      setEditFolderName('');
    } catch (error) {
      toast.error('Failed to rename folder');
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/contacts/folders/${folderId}`, getAuthHeaders());
      toast.success('Folder deleted!');
      fetchFolders();
      if (selectedFolder !== 'All') {
        setSelectedFolder('All');
      }
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Scheduled follow-ups (auto-appear when a follow-up date is set)
  const OFFSET_LABELS = {
    at_time: 'At time of event', '10min': '10 minutes before', '30min': '30 minutes before',
    '1hour': '1 hour before', '2hour': '2 hours before', none: 'No reminder',
  };
  const fmtTime12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = parseInt(h, 10);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    return `${hr % 12 || 12}:${m} ${ampm}`;
  };
  const scheduledFollowUps = contacts
    .filter((c) => c.followup_date)
    .sort((a, b) => `${a.followup_date}T${a.followup_time || '00:00'}`.localeCompare(`${b.followup_date}T${b.followup_time || '00:00'}`));
  const followUpCount = scheduledFollowUps.length;

  const groupLabel = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    if (dateStr < today) return 'Overdue';
    if (dateStr === today) return 'Today';
    if (dateStr === tomorrow) return 'Tomorrow';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };
  const followUpGroups = scheduledFollowUps.reduce((acc, c) => {
    const key = groupLabel(c.followup_date);
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});

  // Filter contacts based on selected folder, follow up toggle, and search query
  const filteredContacts = contacts.filter(c => {
    // First apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const nameMatch = c.name?.toLowerCase().includes(query);
      const businessMatch = c.business?.toLowerCase().includes(query);
      const emailMatch = c.email?.toLowerCase().includes(query);
      if (!nameMatch && !businessMatch && !emailMatch) return false;
    }
    // Then apply follow up filter if enabled
    if (showFollowUpOnly && !c.follow_up) return false;
    // Then apply folder filter
    if (selectedFolder === 'All') return true;
    return c.folder === selectedFolder;
  });

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto" data-testid="contacts-tab">
      {/* Header */}
      <div className="text-center py-4">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-2xl mb-3">
          <Users className="w-6 h-6 text-pink-600 dark:text-pink-400" />
        </div>
        <h1 className="text-2xl font-outfit font-bold text-pink-600 dark:text-pink-400">Contacts</h1>
      </div>

      {/* Follow Up Toggle Button - Always visible */}
      <Button
        variant={showFollowUpOnly ? "default" : "outline"}
        className={`w-full ${showFollowUpOnly 
          ? "bg-amber-500 hover:bg-amber-600 text-white" 
          : "border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"}`}
        onClick={() => setShowFollowUpOnly(!showFollowUpOnly)}
        data-testid="followup-filter-btn"
      >
        <Star className={`w-5 h-5 mr-2 ${showFollowUpOnly ? 'fill-white' : 'fill-amber-400'}`} />
        {showFollowUpOnly ? `Showing Follow Up (${followUpCount})` : `Follow Up (${followUpCount})`}
      </Button>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, business, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 border-pink-200 dark:border-pink-800 focus:border-pink-400"
          data-testid="contact-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Folder Selection and Actions */}
      <Card className="border-pink-200 dark:border-pink-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={selectedFolder} onValueChange={setSelectedFolder}>
              <SelectTrigger className="w-[180px] border-pink-200 dark:border-pink-800" data-testid="folder-select">
                <SelectValue placeholder="Select folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Contacts</SelectItem>
                {folders.map((folder) => (
                  <SelectItem key={folder.folder_id} value={folder.name}>
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Dialog open={isManageFoldersOpen} onOpenChange={setIsManageFoldersOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="icon" className="border-pink-200" data-testid="manage-folders-btn">
                  <Folder className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Manage Folders</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="New folder name"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      data-testid="new-folder-input"
                    />
                    <Button onClick={createFolder} className="bg-pink-600 hover:bg-pink-700" data-testid="create-folder-btn">
                      <FolderPlus className="w-4 h-4" />
                    </Button>
                  </div>
                  <ScrollArea className="h-[200px]">
                    {folders.map((folder) => (
                      <div key={folder.folder_id} className="flex items-center justify-between py-2 border-b">
                        {editingFolder === folder.folder_id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={editFolderName}
                              onChange={(e) => setEditFolderName(e.target.value)}
                              className="flex-1"
                              autoFocus
                            />
                            <Button size="icon" variant="ghost" onClick={() => saveEditFolder(folder.folder_id)}>
                              <Check className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={cancelEditFolder}>
                              <X className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <span>{folder.name}</span>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" onClick={() => startEditFolder(folder)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => deleteFolder(folder.folder_id)}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              </DialogContent>
            </Dialog>
            
            <div className="flex-1" />
            
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-pink-600 hover:bg-pink-700" data-testid="add-contact-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingContact ? 'Edit Contact' : 'New Contact'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Contact name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business">Business</Label>
                    <Input
                      id="business"
                      value={formData.business}
                      onChange={(e) => setFormData({ ...formData, business: e.target.value })}
                      placeholder="Company or business name"
                      data-testid="contact-business-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Phone number"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Email address"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebook_url">Social Media URL</Label>
                    <Input
                      id="facebook_url"
                      value={formData.facebook_url}
                      onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                      placeholder="Facebook, LinkedIn, etc."
                      data-testid="contact-facebook-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="folder">Folder</Label>
                    <Select value={formData.folder} onValueChange={(v) => setFormData({ ...formData, folder: v })}>
                      <SelectTrigger data-testid="contact-folder-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General">General</SelectItem>
                        {folders.filter((f) => f.name !== 'General').map((folder) => (
                          <SelectItem key={folder.folder_id} value={folder.name}>
                            {folder.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Add notes about this contact"
                      data-testid="contact-notes-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="Comma-separated, e.g. Customer, VIP"
                      data-testid="contact-tags-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">Follow-Up & Reminder</Label>
                    <ReminderField
                      value={{ date: formData.followup_date, time: formData.followup_time, offset: formData.followup_reminder }}
                      onChange={(v) => setFormData({ ...formData, followup_date: v.date, followup_time: v.time, followup_reminder: v.offset })}
                      accent="pink"
                      dateLabel="Follow-up date"
                      timeLabel="Follow-up time"
                      testPrefix="contact-followup"
                    />
                  </div>
                  <Button onClick={handleSubmit} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="save-contact-btn">
                    {editingContact ? 'Update Contact' : 'Create Contact'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Import Contacts */}
      <Card className="border-pink-200 dark:border-pink-800" data-testid="import-section">
        <CardContent className="p-4 space-y-3">
          <Button
            className="w-full bg-pink-600 hover:bg-pink-700"
            onClick={() => setIsImportOpen(true)}
            data-testid="import-contacts-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> Import Contacts
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Import contacts from a CSV file. Export from Project Broadcast, Google Contacts, spreadsheets, CRMs, or other platforms and bring them into BOB.
          </p>
          {imports.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="w-full flex items-center justify-center gap-1 text-xs text-pink-600 hover:underline"
                data-testid="import-history-toggle"
              >
                <History className="w-3.5 h-3.5" />
                {showHistory ? 'Hide' : 'Show'} import history ({imports.length})
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2" data-testid="import-history-list">
                  {imports.map((imp) => (
                    <div key={imp.batch_id} className="rounded-xl border p-3 text-sm bg-slate-50 dark:bg-slate-900/40" data-testid={`import-history-${imp.batch_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{imp.source || imp.filename || 'CSV Import'}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {new Date(imp.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                            {imp.filename ? ` · ${imp.filename}` : ''}
                          </p>
                          {imp.status === 'processing' ? (
                            <p className="text-[11px] text-pink-600 mt-0.5 font-medium">
                              Importing… {(imp.processed || 0).toLocaleString()} / {(imp.total || 0).toLocaleString()} ({imp.percent || 0}%)
                            </p>
                          ) : (
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {imp.imported} added
                              {imp.updated ? ` • ${imp.updated} updated` : ''}
                              {imp.skipped ? ` • ${imp.skipped} skipped` : ''}
                              {(imp.invalid || imp.failed) ? ` • ${(imp.invalid || 0) + (imp.failed || 0)} errors` : ''}
                            </p>
                          )}
                        </div>
                        {imp.status === 'processing' ? (
                          <span className="text-[10px] px-2 py-1 rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/40 shrink-0 flex items-center gap-1" data-testid={`import-status-${imp.batch_id}`}>
                            <Loader2 className="w-3 h-3 animate-spin" /> Running
                          </span>
                        ) : imp.status === 'failed' ? (
                          <span className="text-[10px] px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 shrink-0" data-testid={`import-status-${imp.batch_id}`}>Paused</span>
                        ) : imp.undone ? (
                          <span className="text-[10px] px-2 py-1 rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700 shrink-0">Undone</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 shrink-0 h-7"
                            onClick={() => undoImport(imp.batch_id)}
                            data-testid={`undo-import-${imp.batch_id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Undo
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Follow Up folder view */}
      {showFollowUpOnly && (
        <Card className="border-amber-300 dark:border-amber-800" data-testid="followup-view">
          <CardContent className="p-0">
            <ScrollArea className="max-h-[65vh]">
              {scheduledFollowUps.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Star className="w-12 h-12 mx-auto mb-3 text-amber-300" />
                  <p>No upcoming follow-ups</p>
                  <p className="text-sm mt-1">Add a follow-up date &amp; time to any contact</p>
                </div>
              ) : (
                <div className="p-3 space-y-5">
                  {Object.entries(followUpGroups).map(([label, items]) => (
                    <div key={label} data-testid={`followup-group-${label}`}>
                      <h3 className={`text-xs font-bold uppercase tracking-wide mb-2 ${label === 'Overdue' ? 'text-red-500' : 'text-amber-600'}`}>
                        {label}
                      </h3>
                      <div className="space-y-2">
                        {items.map((c) => (
                          <div key={c.contact_id} className="rounded-xl border border-amber-200 dark:border-amber-900 p-3 bg-amber-50/40 dark:bg-amber-900/10" data-testid={`followup-card-${c.contact_id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-3 min-w-0">
                                <Avatar className="h-10 w-10 border-2 border-amber-200">
                                  {c.picture_url ? <AvatarImage src={c.picture_url} alt={c.name} /> : null}
                                  <AvatarFallback className="bg-amber-100 text-amber-700 font-semibold text-sm">{getInitials(c.name)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="font-semibold truncate">{c.name}</p>
                                  <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5" />
                                    {c.followup_time ? fmtTime12(c.followup_time) : 'All day'}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                    <Bell className="w-3 h-3" /> {OFFSET_LABELS[c.reminder_offset] || 'No reminder'}
                                  </p>
                                  {c.notes && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.notes}</p>}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => completeFollowUp(c.contact_id)} data-testid={`complete-followup-${c.contact_id}`}>
                                <Check className="w-3.5 h-3.5 mr-1" /> Mark complete
                              </Button>
                              <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 h-8" onClick={() => openEditDialog(c)} data-testid={`edit-followup-${c.contact_id}`}>
                                <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Contacts List */}
      {!showFollowUpOnly && (
      <Card className="border-pink-200 dark:border-pink-800">
        <CardContent className="p-0">
          <ScrollArea className="max-h-[60vh]">
            {filteredContacts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {showFollowUpOnly ? (
                  <>
                    <Star className="w-12 h-12 mx-auto mb-3 text-amber-300" />
                    <p>No contacts in Follow Up</p>
                    <p className="text-sm mt-1">Tap the star on any contact to add them</p>
                  </>
                ) : (
                  <>
                    <Users className="w-12 h-12 mx-auto mb-3 text-pink-300" />
                    <p>No contacts yet</p>
                    <p className="text-sm mt-1">Add your first contact above</p>
                  </>
                )}
              </div>
            ) : (
              <AnimatePresence>
                {filteredContacts.map((contact, index) => (
                  <motion.div
                    key={contact.contact_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-pink-100 dark:border-pink-900 last:border-0"
                  >
                    <div 
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-pink-50 dark:hover:bg-pink-900/20"
                      onClick={() => setExpandedContact(expandedContact === contact.contact_id ? null : contact.contact_id)}
                    >
                      {/* Star Toggle for Follow Up */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleFollowUp(contact.contact_id);
                        }}
                        className={`p-2 rounded-full transition-all ${
                          contact.follow_up 
                            ? 'bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/50' 
                            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700'
                        }`}
                        data-testid={`star-${contact.contact_id}`}
                        title={contact.follow_up ? 'Remove from Follow Up' : 'Add to Follow Up'}
                      >
                        <Star className={`w-5 h-5 transition-colors ${
                          contact.follow_up 
                            ? 'text-amber-500 fill-amber-500' 
                            : 'text-gray-400 dark:text-gray-500'
                        }`} />
                      </button>
                      
                      <Avatar className="h-12 w-12 border-2 border-pink-200">
                        {contact.picture_url ? (
                          <AvatarImage src={contact.picture_url} alt={contact.name} />
                        ) : null}
                        <AvatarFallback className="bg-pink-100 text-pink-600 font-semibold">
                          {getInitials(contact.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{contact.name}</p>
                        {contact.business && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {contact.business}
                          </p>
                        )}
                      </div>
                      {contact.followup_date && (
                        <span className="text-xs text-pink-600 flex items-center gap-1">
                          <Bell className="w-3 h-3" />
                          {new Date(contact.followup_date).toLocaleDateString()}
                        </span>
                      )}
                      {expandedContact === contact.contact_id ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    
                    {/* Expanded Contact Details */}
                    <AnimatePresence>
                      {expandedContact === contact.contact_id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-2 bg-pink-50/50 dark:bg-pink-900/10 space-y-3">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              {contact.phone && (
                                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-pink-600">
                                  <Phone className="w-4 h-4" />
                                  {contact.phone}
                                </a>
                              )}
                              {contact.email && (
                                <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-pink-600">
                                  <Mail className="w-4 h-4" />
                                  {contact.email}
                                </a>
                              )}
                              {contact.facebook_url && (
                                <button 
                                  onClick={() => openExternalLink(contact.facebook_url)} 
                                  className="flex items-center gap-2 text-sm hover:text-pink-600 text-left"
                                >
                                  <Facebook className="w-4 h-4" />
                                  Social Profile
                                </button>
                              )}
                            </div>
                            {contact.notes && (
                              <p className="text-sm text-muted-foreground bg-white dark:bg-slate-800 p-2 rounded">
                                {contact.notes}
                              </p>
                            )}
                            {contact.tags && contact.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1" data-testid={`contact-tags-${index}`}>
                                {(contact.tags || []).map((t, ti) => (
                                  <span key={ti} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300">
                                    <Tag className="w-3 h-3" />{t}
                                  </span>
                                ))}
                              </div>
                            )}
                            {(contact.birthday || contact.source) && (
                              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                {contact.birthday && (
                                  <span className="flex items-center gap-1"><Cake className="w-3.5 h-3.5" />{contact.birthday}</span>
                                )}
                                {contact.source && (
                                  <span className="flex items-center gap-1"><Upload className="w-3.5 h-3.5" />{contact.source}</span>
                                )}
                              </div>
                            )}
                            {contact.custom_fields && Object.keys(contact.custom_fields).length > 0 && (
                              <div className="text-xs bg-white dark:bg-slate-800 rounded p-2 space-y-0.5">
                                {Object.entries(contact.custom_fields).map(([k, v]) => (
                                  <div key={k} className="flex gap-2">
                                    <span className="font-medium text-muted-foreground">{k}:</span>
                                    <span className="break-words">{v}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {contact.followup_date && (
                              <p className="text-sm text-pink-600 flex items-center gap-1">
                                <Bell className="w-4 h-4" />
                                Follow-up: {new Date(contact.followup_date).toLocaleDateString()}
                                {contact.followup_time && ` at ${contact.followup_time}`}
                              </p>
                            )}
                            <div className="flex gap-2 pt-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDialog(contact)}
                                className="border-pink-300 text-pink-600"
                                data-testid={`edit-contact-${index}`}
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteContact(contact.contact_id)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`delete-contact-${index}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
      )}

      <ContactImportDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        folders={folders}
        onImported={() => { fetchContacts(); fetchFolders(); fetchImports(); }}
      />
    </div>
  );
};

export default ContactsTab;
